import { lazy, Suspense, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Plus, ShieldCheck, MapPin, ExternalLink, KanbanSquare } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import Compensation from "@/components/Compensation";
import WorkArrangementBadge from "@/components/WorkArrangementBadge";
import { hasTravelLabel } from "@/lib/opportunityView";
import ApplicationDocumentLinks from "@/components/ApplicationDocumentLinks";
import TailoredMatchReview from "@/components/TailoredMatchReview";
import ResumeSuggestions from "@/components/ResumeSuggestions";
const EarningsTaxModal = lazy(() => import("@/components/EarningsTaxModal"));

function Dimension({ name, dim }) {
  const score = dim.score;
  const color = score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={`dimension-${name}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium capitalize">{name}</span>
        <span className="font-mono font-bold text-sm">{score == null ? "Unknown" : `${Math.round(score)}%`}</span>
      </div>
      {score != null && <div className="h-2 rounded-full bg-muted overflow-hidden mb-2">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>}
      {dim.detail && <p className="text-xs text-muted-foreground">{dim.detail}</p>}
      {dim.label && <p className="text-[11px] text-muted-foreground italic mt-1 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />{dim.label}</p>}
      {dim.matched?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {dim.matched.map((m, i) => <span key={i} className="rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] px-1.5 py-0.5">{m.term}</span>)}
        </div>
      )}
      {dim.missing?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {dim.missing.map((m, i) => <span key={i} className="rounded bg-red-500/10 text-red-600 dark:text-red-400 text-[11px] px-1.5 py-0.5">missing: {m.term}</span>)}
        </div>
      )}
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [calcOpen, setCalcOpen] = useState(false);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    let active = true;
    api.get(`/jobs/${id}`).then(({ data }) => {
      if (!active) return;
      setJob(data);
      if (data.view_tracking_enabled && !data.first_opened_at) {
        api.post(`/jobs/${id}/opened`).catch((e) => toast.error(`Could not save opened status: ${apiErr(e)}`));
      }
    }).catch((e) => toast.error(apiErr(e)));
    return () => { active = false; };
  }, [id]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      await api.post(`/jobs/${id}/analyze`);
      setRefreshKey((key) => key + 1);
      toast.success("Evidence review refreshed");
      await load();
    }
    catch (e) { toast.error(apiErr(e)); } finally { setAnalyzing(false); }
  };

  const createApp = async () => {
    try {
      const { data } = await api.post("/applications", { job_id: id, stage: "Saved" });
      toast.success("Position added to your Pipeline!");
      nav("/pipeline");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!job || job.id !== id) return <div className="text-sm text-muted-foreground" role="status">Loading opportunity…</div>;
  const match = job.match;

  return (
    <div>
      <button onClick={() => nav(-1)} className="mb-4 flex min-h-11 items-center gap-1 rounded-md px-2 text-sm text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-ring" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="mb-6 rounded-lg border border-border bg-card p-4 [overflow-wrap:anywhere] sm:p-5 lg:sticky lg:top-16 lg:z-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">{job.title}</h1>
            <p className="text-muted-foreground">{job.company_name}</p>
            <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
              {job.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
              {(job.work_model || job.work_logistics) && <WorkArrangementBadge value={job.work_model} title={job.work_logistics?.work_terms} />}
              {hasTravelLabel(job) && <span title={job.work_logistics?.travel_evidence} className="rounded border border-border px-1.5 py-0.5">{job.travel_label}</span>}
              {job.seniority && <span className="rounded border border-border px-1.5 py-0.5">{job.seniority}</span>}
              {job.source_url && <a href={job.source_url} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1 px-2 text-primary underline"><ExternalLink className="h-3 w-3" />Source</a>}
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="w-full sm:w-auto">{job.match_score != null && <div className="font-bold text-primary" data-testid="detail-match-score">{Math.round(job.match_score)}% match</div>}<div className="font-bold text-primary">{job.match_rank ? `#${job.match_rank} match · ` : ""}{job.assessment === "Priority" ? "Priority review" : job.assessment}</div><div className="text-xs text-muted-foreground">Requirement match · not hiring probability</div></div>
            <Button className="min-h-11" variant="outline" onClick={analyze} disabled={analyzing || !job.can_refresh_analysis}
              aria-busy={analyzing} data-testid="analyze-btn"><Sparkles className="mr-1 h-4 w-4" />Analyze fit</Button>
            {job.in_pipeline ? (
              <Button
                onClick={() => nav("/pipeline")}
                variant="outline"
                className="min-h-11 border-primary/60 text-primary hover:bg-primary/10 font-semibold"
                data-testid="view-application-btn"
                title="This position is in your pipeline. Click to view on Kanban board"
              >
                <KanbanSquare className="h-4 w-4 mr-1.5" /> In Pipeline ({job.application_stage || "Saved"})
              </Button>
            ) : (
              <Button
                onClick={createApp}
                disabled={job.status === "archived"}
                data-testid="create-application-btn"
                className="min-h-11 bg-primary text-primary-foreground font-semibold hover:bg-primary/90 shadow-sm"
                title="Add this position to your Pipeline journey to track and manage application stages"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add to Pipeline
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 [&_a]:min-h-11 [&_a]:min-w-11 [&_button]:min-h-11 [&_button]:min-w-11">
        <ApplicationDocumentLinks documents={job.documents} jobId={job.id} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <h3 className="font-semibold">Opportunity Analyzer</h3>
          <div className="rounded-lg border border-border bg-card p-4 text-sm space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div><b>Payment:</b> <Compensation review={job.review} details /></div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalcOpen(true)}
                data-testid="detail-tax-calc-btn"
                className="min-h-11 whitespace-normal"
              >
                <span className="font-bold font-mono">$</span>
                <span>Earnings & Tax Calculator</span>
              </Button>
            </div>
            <p><b>Work terms:</b> {job.review?.work_terms || "Unknown"}</p>
            <p><b>Why it fits:</b> {job.review?.why_fit || "Needs review"}</p>
            <p><b>Required proof / gaps:</b> {job.review?.material_gaps || job.review?.reason || "Unknown"}</p>
            <p><b>ERP / certificates:</b> {job.review?.credential_vendor_rule || "Unknown"}</p>
            <p className="text-xs text-muted-foreground">{job.review?.checked_at ? `Source reviewed ${job.review.checked_at}. ` : ""}{job.review?.availability || "Current employer hiring status requires confirmation."}</p>
          </div>
          <ResumeSuggestions key={id} jobId={id} refreshKey={refreshKey} />
          {match && (
            <>
              <p className="text-sm text-muted-foreground rounded-lg border border-border bg-card p-4">{match.summary}</p>
              <TailoredMatchReview match={match} />
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(match.dimensions || {}).map(([name, dim]) => <Dimension key={name} name={name} dim={dim} />)}
              </div>
            </>
          )}
        </div>
        <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]">
          <h3 className="font-semibold">Description snapshot</h3>
          <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground whitespace-pre-wrap max-h-80 overflow-y-auto">
            {job.description_snapshot || "No source snapshot — archived reported description remains unverified."}
          </div>
        </div>
      </div>
      {calcOpen && <Suspense fallback={<p role="status">Loading earnings and tax calculator…</p>}>
        <EarningsTaxModal job={job} open={calcOpen} onOpenChange={setCalcOpen} />
      </Suspense>}
    </div>
  );
}
